import { HttpClient } from '@angular/common/http';
import { computed, Inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT } from 'environments-lib';
import { IEnvironment } from 'interfaces-lib';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  static readonly FLAGS = {
    templateListPaperclipIcon: 'feature_kad2_2430_template_list_paperclip_icon',
    salesforceRelatedTemplates: 'salesforce-related-templates',
    jobSubmissionOnlineResponse: 'feature_ai_1121_job_submission_online_response',
    aiaExpandedFileFormats: 'feature_ai_1194_aia_expanded_file_formats',
    directOutputFileSave: 'feature_kad2_2794_document_options',
    editEnvelopeInDocusign: 'feature_kad2_2906_edit_envelope_in_docusign',
    wordAddinPasswordSignIn: 'feature_kad2_2859_word_addin_password_sign_in',
    oneNotePerCall: 'feature_ai_1175_one_note_per_call',
    customSummaryPrompt: 'feature_ai_1314_custom_summary_prompt',
    createTemplateAsDifferentUser: 'feature_kad2_1084_create_template_as_different_user',
    templateDirectUrl: 'feature_kad2_1876_template_direct_url',
    templateDirectUrlDocMerge: 'feature_kad2_1876_template_direct_url_docmerge',
    percentageNumberField: 'feature_kad2_2290_percentage_number_field'
  } as const;

  private flagValues: Record<string, boolean> = {};
  private readonly ready$$ = new ReplaySubject<void>(1);
  private readonly flagValuesSignal = toSignal(this.ready$$.pipe(map(() => this.flagValues)), {
    initialValue: {} as Record<string, boolean>,
  });

  constructor(
    @Inject(ENVIRONMENT) protected environment: Partial<IEnvironment>,
    private http: HttpClient,
  ) {}

  init(context: { environment: string; tenant_id: string; user_id?: string; email?: string }): Observable<void> {
    return this.http
      .post(this.environment.featureFlagsUrl, context, { headers: { 'x-api-key': this.environment.featureFlagsApiKey } })
      .pipe(
        map((response: any) => {
          this.flagValues = response;
        }),
        catchError((err) => {
          console.warn('Failed to initialize feature flags.', err);
          return of(undefined); // of(void 0) also works
        }),
        // Mark ready on both the success and the swallowed-error path so consumers never wait forever on a backend outage.
        tap(() => this.ready$$.next()),
      );
  }

  enabled(key: string): Signal<boolean> {
    return computed(() => this.flagValuesSignal()[key] === true);
  }

  /**
   * Completes once init() has settled, whether it succeeded or failed.
   */
  ready(): Observable<void> {
    return this.ready$$.pipe(take(1));
  }
}
